import { createSelector } from 'reselect'

import { urlPointsToPlottrCloud } from '../helpers/file'

import {
  backupEnabledSelector,
  localBackupsEnabledSelector,
  previouslyLoggedIntoProSelector,
  choseTrialModeSelector,
  choseProModeSelector,
  chosePlottrModeSelector,
  offlineModeV2EnabledSelector,
} from './settingsFirstOrder'
import {
  applicationSettingsAreLoadedSelector,
  checkedLicenseSelector,
  checkedProSubscriptionSelector,
  checkedTrialSelector,
  checkingWhatToLoadOrNeedToCheckWhatToLoadSelector,
  importingNewProjectSelector,
  isOnboardingToProSelector,
  manipulatingAFileSelector,
  sessionCheckedSelector,
  checkingSessionSelector,
  loadingFileSelector,
  proFileListIsLoadingSelector,
  plottrFileListIsLoadingSelector,
  isQuittingSelector,
} from './applicationStateFirstOrder'
import { isLoggedInSelector, isOnWebSelector } from './clientFirstOrder'
import {
  isOfflineSelector,
  isResumingSelector,
  fileURLSelector,
  hasAllKeysSelector,
} from './projectFirstOrder'
import {
  hasActivePlottrLicenseSelector,
  hasActiveProLicenseSelector,
  trialExpiredSelector,
  trialStartedSelector,
} from './licenseFirstOrder'
import { fileIsLoadedSelector } from './applicationStateFirstOrder'
import { shouldBeInProSelector, isInOfflineModeSelector } from './secondOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 */

/**
 * @type {function(PlottrState): boolean}
 */
export const fileListIsLoadingSelector = createSelector(
  choseProModeSelector,
  chosePlottrModeSelector,
  proFileListIsLoadingSelector,
  plottrFileListIsLoadingSelector,
  (chosePro, chosePlottr, proFilesLoading, plottrFilesLoading) => {
    return (chosePro && proFilesLoading) || (chosePlottr && plottrFilesLoading)
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const userNeedsToLoginSelector = createSelector(
  applicationSettingsAreLoadedSelector,
  shouldBeInProSelector,
  sessionCheckedSelector,
  checkingSessionSelector,
  isLoggedInSelector,
  (settingsAreLoaded, shouldBeInPro, sessionChecked, checkingSession, isLoggedIn) => {
    return settingsAreLoaded && shouldBeInPro && !checkingSession && sessionChecked && !isLoggedIn
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const needToCheckProSubscriptionSelector = createSelector(
  shouldBeInProSelector,
  checkedProSubscriptionSelector,
  (shouldBeInPro, checkedProSubscription) => {
    return shouldBeInPro && !checkedProSubscription
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isInTrialModeSelector = createSelector(
  trialStartedSelector,
  trialExpiredSelector,
  hasActivePlottrLicenseSelector,
  shouldBeInProSelector,
  choseTrialModeSelector,
  (started, trialExpired, hasLicense, shouldBeInPro, choseTrialMode) => {
    return started && !trialExpired && !hasLicense && !shouldBeInPro && choseTrialMode
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isInSomeValidLicenseStateSelector = createSelector(
  applicationSettingsAreLoadedSelector,
  sessionCheckedSelector,
  userNeedsToLoginSelector,
  isInOfflineModeSelector,
  needToCheckProSubscriptionSelector,
  hasActiveProLicenseSelector,
  hasActivePlottrLicenseSelector,
  isInTrialModeSelector,
  isLoggedInSelector,
  shouldBeInProSelector,
  (
    applicationSettingsAreLoaded,
    sessionChecked,
    needsToLogin,
    isInOfflineMode,
    needToCheckProSubscription,
    hasProLicense,
    hasPlottrLicense,
    isInTrialMode,
    isLoggedIn,
    shouldBeInPro
  ) => {
    return (
      applicationSettingsAreLoaded &&
      (isInOfflineMode ||
        (sessionChecked &&
          !needsToLogin &&
          !needToCheckProSubscription &&
          ((isLoggedIn && hasProLicense) ||
            (!shouldBeInPro && (hasPlottrLicense || isInTrialMode)))))
    )
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const checkingSessionOrNeedToCheckSessionSelector = createSelector(
  sessionCheckedSelector,
  checkingSessionSelector,
  isInOfflineModeSelector,
  (sessionChecked, checkingSession, isInOfflineMode) => {
    return !isInOfflineMode && (checkingSession || !sessionChecked)
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const busyLoadingFileOrNeedToLoadFileSelector = createSelector(
  loadingFileSelector,
  fileIsLoadedSelector,
  (fileIsLoading, loadedAFileBefore) => {
    return fileIsLoading || !loadedAFileBefore
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const applicationIsBusyAndUninterruptableSelector = createSelector(
  busyLoadingFileOrNeedToLoadFileSelector,
  checkingWhatToLoadOrNeedToCheckWhatToLoadSelector,
  manipulatingAFileSelector,
  applicationSettingsAreLoadedSelector,
  checkingSessionOrNeedToCheckSessionSelector,
  isOnboardingToProSelector,
  importingNewProjectSelector,
  isLoggedInSelector,
  shouldBeInProSelector,
  checkedProSubscriptionSelector,
  checkedLicenseSelector,
  checkedTrialSelector,
  isQuittingSelector,
  (
    busyLoadingFileOrNeedToLoadFile,
    checkingWhatToLoadOrNeedToCheckWhatToLoad,
    manipulatingAFile,
    applicationSettingsAreLoaded,
    checkingSessionOrNeedToCheckSession,
    isOnboardingToPro,
    isImportingAProject,
    isLoggedIn,
    shouldBeInPro,
    checkedProSubscription,
    checkedLicense,
    checkedTrial,
    isQuitting
  ) => {
    return (
      busyLoadingFileOrNeedToLoadFile ||
      checkingWhatToLoadOrNeedToCheckWhatToLoad ||
      manipulatingAFile ||
      !applicationSettingsAreLoaded ||
      (checkingSessionOrNeedToCheckSession && !isOnboardingToPro) ||
      isImportingAProject ||
      (isLoggedIn && shouldBeInPro && !checkedProSubscription) ||
      // TODO: Web doesn't have trials or licenses to load.
      !checkedLicense ||
      !checkedTrial ||
      isQuitting
    )
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isFirstTimeSelector = createSelector(
  hasActivePlottrLicenseSelector,
  trialStartedSelector,
  hasActiveProLicenseSelector,
  shouldBeInProSelector,
  (hasLicense, trialStarted, hasCurrentProLicense, shouldBeInPro) => {
    return !hasLicense && !trialStarted && !hasCurrentProLicense && !shouldBeInPro
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isInTrialModeWithExpiredTrialSelector = createSelector(
  trialStartedSelector,
  trialExpiredSelector,
  hasActivePlottrLicenseSelector,
  hasActiveProLicenseSelector,
  checkedProSubscriptionSelector,
  previouslyLoggedIntoProSelector,
  checkedLicenseSelector,
  checkingSessionOrNeedToCheckSessionSelector,
  (
    started,
    trialExpired,
    hasLicense,
    hasCurrentProLicense,
    checkedPro,
    choseProMode,
    checkedLicense,
    checkingSessionOrNeedToCheckSession
  ) => {
    return (
      checkedPro &&
      checkedLicense &&
      !checkingSessionOrNeedToCheckSession &&
      started &&
      trialExpired &&
      !hasLicense &&
      (!hasCurrentProLicense || (hasCurrentProLicense && !choseProMode))
    )
  }
)

// NOTE(ed): Used by web only.
//
// TODO(ed): Overhaul web's startup sequence.

/**
 * @type {function(PlottrState): boolean}
 */
export const applicationIsBusyButFileCouldBeUnloadedSelector = createSelector(
  isFirstTimeSelector,
  isInTrialModeWithExpiredTrialSelector,
  checkingWhatToLoadOrNeedToCheckWhatToLoadSelector,
  loadingFileSelector,
  manipulatingAFileSelector,
  applicationSettingsAreLoadedSelector,
  isInOfflineModeSelector,
  checkingSessionOrNeedToCheckSessionSelector,
  isLoggedInSelector,
  shouldBeInProSelector,
  checkedProSubscriptionSelector,
  (
    firstTime,
    isInTrialModeWithExpiredTrial,
    checkingWhatToLoadOrNeedToCheckWhatToLoad,
    loadingFile,
    manipulatingAFile,
    applicationSettingsAreLoaded,
    isInOfflineMode,
    checkingSessionOrNeedToCheckSession,
    isLoggedIn,
    shouldBeInPro,
    checkedProSubscription
  ) => {
    return (
      // We only check what to load when we're in a valid license
      // state.  So we need to account for there being no license or
      // there being an expired trial.
      (!firstTime && !isInTrialModeWithExpiredTrial && checkingWhatToLoadOrNeedToCheckWhatToLoad) ||
      loadingFile ||
      manipulatingAFile ||
      !applicationSettingsAreLoaded ||
      (!isInOfflineMode && checkingSessionOrNeedToCheckSession) ||
      (isLoggedIn && shouldBeInPro && !checkedProSubscription)
    )
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const isCloudFileSelector = createSelector(
  fileURLSelector,
  isOnWebSelector,
  (fileURL, isOnWeb) => {
    return !!(isOnWeb || (fileURL && urlPointsToPlottrCloud(fileURL)))
  }
)

// NOTE(ed): Used by web only.
//
// TODO(ed): Overhaul web's startup sequence.

/**
 * @type {function(PlottrState): boolean}
 */
export const cantShowFileSelector = createSelector(
  fileIsLoadedSelector,
  hasActiveProLicenseSelector,
  isCloudFileSelector,
  isResumingSelector,
  isOfflineSelector,
  isInOfflineModeSelector,
  shouldBeInProSelector,
  (
    fileLoaded,
    hasActiveProSubscription,
    selectedFileIsACloudFile,
    isResuming,
    isOffline,
    isInOfflineMode,
    shouldBeInPro
  ) => {
    return (
      !isResuming &&
      (!fileLoaded ||
        (!isInOfflineMode && isOffline && shouldBeInPro) ||
        (!isInOfflineMode && !!hasActiveProSubscription !== !!selectedFileIsACloudFile))
    )
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const canSaveSelector = createSelector(
  fileURLSelector,
  isResumingSelector,
  hasAllKeysSelector,
  (fileURL, isResuming, hasAllKeys) => {
    return !!fileURL && !isResuming && hasAllKeys
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const shouldSaveOfflineFileSelector = createSelector(
  offlineModeV2EnabledSelector,
  canSaveSelector,
  isCloudFileSelector,
  (offlineModeEnabled, canSave, isCloudFile) => {
    return offlineModeEnabled && isCloudFile && canSave
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const canBackupSelector = createSelector(
  fileURLSelector,
  isResumingSelector,
  isOfflineSelector,
  hasAllKeysSelector,
  isCloudFileSelector,
  localBackupsEnabledSelector,
  backupEnabledSelector,
  (fileURL, isResuming, isOffline, hasAllKeys, isCloudFile, localBackupsEnabled, backupEnabled) => {
    return (
      backupEnabled &&
      !!fileURL &&
      !isResuming &&
      !(isOffline && isCloudFile) &&
      ((isCloudFile && localBackupsEnabled) || !isCloudFile) &&
      hasAllKeys
    )
  }
)
